import { IconComponent, type IconeNome } from '@/shared/components/ui/icon';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  InjectionToken,
  booleanAttribute,
  computed,
  inject,
  input,
} from '@angular/core';

/** Contrato que o `app-menu` expõe aos itens (sem os itens importarem a classe do menu). */
export interface MenuApi {
  /** Fecha o menu; `devolverFoco` volta o foco para o gatilho. */
  fechar(devolverFoco?: boolean): void;
}

export const MENU = new InjectionToken<MenuApi>('MENU');

// Altura e espaçamento vêm de `iconeCaixa` (ver `classes`). `aria-[current=page]` destaca a rota atual.
const CLASSES_ITEM =
  'flex w-full items-center rounded-lg px-[0.625rem] border-solid border-[0px] ' +
  'text-sm text-rfy-foreground no-underline cursor-pointer hover:bg-rfy-subtle hover:text-rfy-foreground ' +
  'focus-visible:outline-none focus-visible:bg-rfy-subtle disabled:cursor-not-allowed disabled:opacity-60 ' +
  'aria-[current=page]:bg-rfy-primary/10 aria-[current=page]:font-semibold aria-[current=page]:text-rfy-foreground-strong';

const CLASSES_ITEM_MARCADO = 'bg-rfy-primary/10 font-semibold';

/**
 * Item de um `app-menu` (`button[app-menu-item]` ou `a[app-menu-item]`).
 * Sem `marcado` é um `menuitem`; com `marcado` (boolean) vira `menuitemradio`.
 */
@Component({
  selector: 'button[app-menu-item], a[app-menu-item]',
  standalone: true,
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (icone(); as nome) {
      @if (iconeCaixa()) {
        <span
          class="inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-rfy-primary/15 text-rfy-primary"
        >
          <app-icon [nome]="nome" tamanho="sm" />
        </span>
      } @else {
        <app-icon [nome]="nome" tamanho="sm" class="text-rfy-muted" />
      }
    }
    <span class="min-w-0 flex-1 truncate text-left"><ng-content></ng-content></span>
    @if (marcado()) {
      <app-icon nome="check" tamanho="sm" class="text-rfy-primary" />
    }
  `,
  host: {
    '[class]': 'classes()',
    '[attr.role]': 'marcado() === undefined ? "menuitem" : "menuitemradio"',
    '[attr.aria-checked]': 'marcado() === undefined ? null : marcado() ? "true" : "false"',
    tabindex: '-1',
    '(click)': 'aoClicar()',
  },
})
export class MenuItemComponent {
  /** Elemento host, usado pelo menu para a navegação por teclado. */
  readonly element: HTMLElement = inject(ElementRef).nativeElement;
  private readonly menu = inject(MENU, { optional: true });

  readonly icone = input<IconeNome>();
  /** `undefined` = item comum; boolean = item de rádio. */
  readonly marcado = input<boolean | undefined>();
  /** Mantém o menu aberto depois do clique. */
  readonly manterAberto = input(false, { transform: booleanAttribute });
  /** Ícone num quadrado de destaque e item mais alto (navegação lateral). */
  readonly iconeCaixa = input(false, { transform: booleanAttribute });

  protected readonly classes = computed(() => {
    // `bg-[transparent]` só sem marcação, para não disputar com o fundo do item marcado.
    // `gap-3` tem homônimo no Bootstrap (1rem), por isso o valor arbitrário.
    const base = this.iconeCaixa() ? `${CLASSES_ITEM} h-11 gap-[0.75rem]` : `${CLASSES_ITEM} h-9 gap-2`;
    return this.marcado() === true ? `${base} ${CLASSES_ITEM_MARCADO}` : `${base} bg-[transparent]`;
  });

  focar(): void {
    this.element.focus();
  }

  protected aoClicar(): void {
    if (!this.manterAberto()) this.menu?.fechar(true);
  }
}
